import type { WebPushMaterial, WebPushRecoveryResult } from "@garimpa/lib/webPushRecovery";

interface PushRecoveryState {
  generation: number;
  confirmed: (WebPushRecoveryResult & { material: WebPushMaterial }) | null;
  stop: "disabled" | "detached" | null;
  error: string | null;
}
interface PushRecoveryClient {
  saveConfirmed(input: {
    result: WebPushRecoveryResult;
    material: WebPushMaterial;
    vapidPublicKey: string;
    generation?: number;
  }): Promise<boolean>;
  setLocalStop(stop: "disabled" | "detached"): Promise<void>;
  readState(): Promise<PushRecoveryState>;
  synchronize(input: {
    registration: ServiceWorkerRegistration;
    newSubscription?: PushSubscription | null;
    oldSubscription?: PushSubscription | null;
  }): Promise<{ status: string }>;
}

let loading: Promise<PushRecoveryClient> | undefined;

/** Loads the same runtime used by the classic worker, once per document. */
export function loadWebPushRecoveryClient(): Promise<PushRecoveryClient> {
  const runtime = (globalThis as typeof globalThis & { garimpaPushRecovery?: PushRecoveryClient })
    .garimpaPushRecovery;
  if (runtime) return Promise.resolve(runtime);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "/push-recovery.js";
    script.onload = () => {
      const client = (
        globalThis as typeof globalThis & { garimpaPushRecovery?: PushRecoveryClient }
      ).garimpaPushRecovery;
      if (client) resolve(client);
      else {
        loading = undefined;
        reject(new Error("Push recovery is unavailable"));
      }
    };
    script.onerror = () => {
      loading = undefined;
      reject(new Error("Push recovery is unavailable"));
    };
    document.head.append(script);
  });
  return loading;
}
